import { Component, Inject, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NodeType } from '@completion/enums';
import { DocumentNode } from '@completion/models';
import { Store } from '@ngrx/store';
import { getCurrentDocument } from '@completion/selectors';
import { DocumentUpdate } from '@completion/actions';

export interface HeaderNode {
  id: number;
  title: string;
  level: number;
  chapterNumber: string;
  children: HeaderNode[];
}

@Component({
  selector: 'app-table-of-contents',
  templateUrl: './table-of-contents.component.html',
  styleUrls: ['./table-of-contents.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableOfContentsComponent implements OnInit, AfterViewInit, OnDestroy {
  nodes: DocumentNode[] = [];
  documentNode: DocumentNode;
  // track the current ProcedureDocument from the store (if any)
  private currentDocument: any = null;

  headers: HeaderNode[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    private store: Store,
  ) {
    this.documentNode = documentNode;

    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => {
        this.currentDocument = doc;
        if (doc) {
          // try direct property first
          let nodes = (doc as any).nodes || [];
          // if not found or empty, search the object for the first array that looks like nodes
          if (!nodes || nodes.length === 0) {
            const found = this.findNodesArray(doc);
            if (found) {
              nodes = found;
            }
          }
          this.nodes = nodes || [];
          // Rebuild headers whenever the document changes
          this.headers = this.buildSectionTree(this.nodes, 1);
          // Fallback: if no sections found, try building TOC from headings (for older documents)
          if (!this.headers || this.headers.length === 0) {
            this.headers = this.buildHeadingTree(this.nodes, 1);
          }
        } else {
          this.nodes = [];
          this.headers = [];
        }
      });
  }

  ngOnInit(): void {
    // initial build (if nodes already set)
    this.headers = this.buildSectionTree(this.nodes, 1);
  }

  // When the document's nodes are changed by this component, call this to patch the
  // current document in the store so all parts of the app have an up-to-date copy.
  public patchDocument(updatedNodes: DocumentNode[]): void {
    if (!updatedNodes) {
      return;
    }
    // We should only patch the full ProcedureDocument in the store. If there is
    // a current document in the store, update its nodes and dispatch an update.
    if (!this.currentDocument) {
      console.warn('patchDocument called but there is no current document in the store. Skipping dispatch.');
      return;
    }

    // Create a shallow copy to preserve immutability semantics expected by ngrx
    const updatedDocument = {
      ...this.currentDocument,
      nodes: Array.isArray(updatedNodes) ? updatedNodes.slice() : updatedNodes,
    } as any;

    this.store.dispatch(new DocumentUpdate(updatedDocument));
  }

  // Scroll smoothly to a section element given its numeric id.
  // Prevents the default anchor jump and updates the URL hash without forcing
  // a layout thrash. Accounts for a top toolbar if present.
  public scrollTo(event?: Event, sectionId?: number | string): void {
    if (event && typeof event.preventDefault === 'function') {
      try {
        event.preventDefault();
      } catch {
        /* ignore */
      }
    }
    if (sectionId === undefined || sectionId === null) {
      return;
    }

    const targetId = `section-${sectionId}`;

    const doScroll = (el: HTMLElement) => {
      // Compute a reasonable offset for a top app bar if present
      let headerOffset = 0;
      const toolbar = document.querySelector('mat-toolbar, .app-toolbar, header, .topbar');
      if (toolbar && toolbar instanceof HTMLElement) {
        headerOffset = (toolbar as HTMLElement).offsetHeight;
      }

      const rect = el.getBoundingClientRect();
      const absoluteY = window.scrollY + rect.top;
      const scrollToY = Math.max(absoluteY - headerOffset - 8, 0); // small padding

      window.scrollTo({ top: scrollToY, behavior: 'smooth' });

      // After scrolling, focus the element for accessibility. Use preventScroll
      // to avoid another jump if supported.
      try {
        // Try focus with preventScroll if supported
        (el as any).focus({ preventScroll: true });
      } catch {
        try {
          el.focus();
        } catch {
          /* ignore */
        }
      }

      // Update the URL hash without causing a jump
      try {
        history.replaceState(null, '', `#${targetId}`);
      } catch {
        // ignore history replace failures in older browsers
      }
    };

    const el = document.getElementById(targetId) as HTMLElement | null;
    if (el) {
      doScroll(el);
      return;
    }

    // If element is not present yet (e.g., not rendered), try a short retry.
    setTimeout(() => {
      const elRetry = document.getElementById(targetId) as HTMLElement | null;
      if (elRetry) {
        doScroll(elRetry);
      } else {
        console.warn('TOC: could not find target element to scroll to:', targetId);
      }
    }, 50);
  }

  ngAfterViewInit(): void {
    // If a deep link to a section is present in the URL on load, scroll to it.
    try {
      const hash = window.location.hash;
      if (hash && hash.startsWith('#section-')) {
        const idPart = hash.replace('#section-', '');
        // Try to scroll after a short delay to let the view render
        setTimeout(() => this.scrollTo(undefined, idPart), 50);
      }
    } catch {
      // ignore
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Build a header tree based on SECTION nodes; nested sections become children
  private buildSectionTree(nodes: DocumentNode[], level: number, parentNumber: string = ''): HeaderNode[] {
    const tree: HeaderNode[] = [];
    let counter = 0;

    for (const node of nodes) {
      if (this.isSection(node)) {
        counter++;
        const chapterNumber = parentNumber ? `${parentNumber}.${counter}` : `${counter}`;
        const title = this.inferSectionTitle(node);
        const header: HeaderNode = {
          id: node.id,
          title,
          level,
          chapterNumber,
          children: this.buildSectionTree(this.getNodeChildren(node), level + 1, chapterNumber),
        };
        tree.push(header);
      } else {
        // If node contains children under different field names, scan them for sections
        const children = this.getNodeChildren(node);
        if (children && children.length) {
          const childSections = this.buildSectionTree(children, level, parentNumber);
          if (childSections.length) {
            tree.push(...childSections);
          }
        }
      }
    }

    return tree;
  }

  // Return node type normalized to uppercase string. Supports several property names.
  private getNodeType(node: any): string {
    if (!node) {
      return '';
    }
    const candidates = [node.type, node.nodeType, node.node_type, node.typeName, node.kind];
    for (const c of candidates) {
      if (c !== undefined && c !== null) {
        return String(c).toUpperCase();
      }
    }
    return '';
  }

  // Return an array of children regardless of property name used by the payload.
  private getNodeChildren(node: any): DocumentNode[] {
    if (!node || typeof node !== 'object') {
      return [];
    }
    return node.children || node.childNodes || node.nodes || node.elements || [];
  }

  // Robust detection whether a node should be considered a SECTION for TOC purposes
  private isSection(node: DocumentNode): boolean {
    if (!node) {
      return false;
    }
    // Prefer explicit type
    const typeStr = this.getNodeType(node);
    if (typeStr === String(NodeType.SECTION)) return true;
    if (typeStr === String(NodeType.TEST_SECTION)) return true;

    // Name-based heuristic
    if (node.name && /section/i.test(node.name)) {
      return true;
    }

    // Child-type heuristic (if it groups authors/reviewers/approvers/cp selector)
    const children = this.getNodeChildren(node);
    if (children && children.length) {
      for (const c of children) {
        const cType = this.getNodeType(c);
        if (
          cType === String(NodeType.AUTHOR) ||
          cType === String(NodeType.REVIEWER) ||
          cType === String(NodeType.APPROVER) ||
          cType === String(NodeType.CP_SELECTOR)
        ) {
          return true;
        }
      }
    }

    return false;
  }

  // Build a header tree based on HEADING nodes (fallback for older documents)
  private buildHeadingTree(nodes: DocumentNode[], level: number, parentNumber: string = ''): HeaderNode[] {
    const tree: HeaderNode[] = [];
    let counter = 0;
    for (const node of nodes) {
      const typeStr = this.getNodeType(node);
      if (typeStr === String(NodeType.HEADING)) {
        counter++;
        const chapterNumber = parentNumber ? `${parentNumber}.${counter}` : `${counter}`;
        const header: HeaderNode = {
          id: node.id,
          title: node.content || node.name || 'Heading',
          level,
          chapterNumber,
          children: [],
        };
        tree.push(header);
        // include headings under this heading from children
        const children = this.getNodeChildren(node);
        if (children && children.length) {
          const childHeaders = this.buildHeadingTree(children, level + 1, chapterNumber);
          if (childHeaders.length) {
            header.children.push(...childHeaders);
          }
        }
      } else {
        const children = this.getNodeChildren(node);
        const childHeaders = this.buildHeadingTree(children, level, parentNumber);
        if (childHeaders.length) {
          tree.push(...childHeaders);
        }
      }
    }
    return tree;
  }

  // Prefer explicit name; otherwise infer from child node types (Authors/Reviewers/Approvers/TOC/CP Selector)
  private inferSectionTitle(node: DocumentNode): string {
    if (node.name && node.name.trim()) {
      return node.name;
    }

    const children = this.getNodeChildren(node);
    const typeStrs = new Set(children.map((c) => this.getNodeType(c)));
    if (typeStrs.has(String(NodeType.AUTHOR))) {
      return 'Authors';
    }
    if (typeStrs.has(String(NodeType.REVIEWER))) {
      return 'Reviewers';
    }
    if (typeStrs.has(String(NodeType.APPROVER))) {
      return 'Approvers';
    }
    if (typeStrs.has(String(NodeType.TABLE_OF_CONTENTS))) {
      return 'Table of Contents';
    }
    if (typeStrs.has(String(NodeType.CP_SELECTOR))) {
      return 'CP Selector';
    }

    // Fallback: use a generic label with id to keep label unique
    return 'Section';
  }

  // Search object recursively for an array that looks like a list of DocumentNode (objects with 'type')
  private findNodesArray(obj: any, depth = 0): any[] | null {
    if (!obj || depth > 6) {
      return null;
    }
    if (Array.isArray(obj)) {
      // check if array elements look like DocumentNode by checking several possible type keys
      const sample = obj.find((el: any) => el && (el.type !== undefined || el.nodeType !== undefined || el.node_type !== undefined));
      if (sample) {
        return obj;
      }
    }
    if (typeof obj === 'object') {
      for (const key of Object.keys(obj)) {
        try {
          const v = obj[key];
          if (v && typeof v === 'object') {
            const found = this.findNodesArray(v, depth + 1);
            if (found) {
              return found;
            }
          }
        } catch {
          // ignore
        }
      }
    }
    return null;
  }
}
